import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lightbulb, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SuggestionsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    suggestion: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email && !formData.phone) {
      toast.error('Por favor, forneça um e-mail ou número de telefone para contato.', {
        duration: 4000,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/suggestion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Sugestão enviada com sucesso! Obrigado pelo seu feedback.', {
          duration: 4000,
        });
        setFormData({ name: '', email: '', phone: '', suggestion: '' });
      } else {
        toast.error(data.detail || 'Erro ao enviar sugestão. Tente novamente.', {
          duration: 4000,
        });
      }
    } catch (error) {
      toast.error('Erro de conexão. Tente novamente mais tarde.', {
        duration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-dark-primary py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-purple-light hover:text-purple-primary transition-colors mb-8">
          <ArrowLeft className="mr-2" size={20} />
          Voltar para Home
        </Link>

        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-primary to-green-primary rounded-full mb-6 animate-float">
            <Lightbulb className="text-white" size={40} />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-archive text-white mb-4 glow-text">
            Compartilhe Sua Ideia
          </h1>
          <p className="text-xl text-gray-400 font-hero max-w-2xl mx-auto">
            Sua opinião é fundamental! Ajude-nos a melhorar os jogos e a plataforma
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="bg-dark-secondary/80 backdrop-blur-xl border-purple-primary/30 shadow-2xl hover:shadow-purple-primary/20 transition-all duration-500">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-3xl font-bold font-archive text-transparent bg-gradient-to-r from-purple-light via-green-primary to-purple-light bg-clip-text animate-gradient">
                Compartilhe Sua Ideia
              </CardTitle>
              <CardDescription className="text-lg text-gray-400 font-hero">
                Sugestões de melhorias para jogos, funcionalidades ou a plataforma
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="suggestion-name" className="text-white font-hero flex items-center gap-2">
                    Nome Completo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="suggestion-name"
                    name="name"
                    type="text"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-dark-primary/80 border-purple-primary/30 text-white placeholder:text-gray-500 focus:border-green-primary focus:ring-2 focus:ring-green-primary/20 transition-all duration-300"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="suggestion-email" className="text-white font-hero flex items-center gap-2">
                      E-mail <span className="text-gray-500 text-sm">(obrigatório se não informar telefone)</span>
                    </Label>
                    <Input
                      id="suggestion-email"
                      name="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-dark-primary/80 border-purple-primary/30 text-white placeholder:text-gray-500 focus:border-green-primary focus:ring-2 focus:ring-green-primary/20 transition-all duration-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="suggestion-phone" className="text-white font-hero flex items-center gap-2">
                      Telefone <span className="text-gray-500 text-sm">(obrigatório se não informar e-mail)</span>
                    </Label>
                    <Input
                      id="suggestion-phone"
                      name="phone"
                      type="tel"
                      placeholder="+55 (11) 99999-9999"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-dark-primary/80 border-purple-primary/30 text-white placeholder:text-gray-500 focus:border-green-primary focus:ring-2 focus:ring-green-primary/20 transition-all duration-300"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="suggestion-text" className="text-white font-hero flex items-center gap-2">
                    Sua Sugestão <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="suggestion-text"
                    name="suggestion"
                    placeholder="Descreva sua sugestão de melhoria para os jogos ou para a plataforma..."
                    value={formData.suggestion}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-dark-primary/80 border-purple-primary/30 text-white placeholder:text-gray-500 focus:border-green-primary focus:ring-2 focus:ring-green-primary/20 resize-none transition-all duration-300"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-purple-primary via-green-primary to-purple-primary bg-size-200 animate-gradient-x hover:shadow-2xl hover:shadow-purple-primary/50 text-white font-semibold py-6 text-lg rounded-xl transition-all duration-300 hover:scale-105 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      Enviar Sugestão
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
